import type { AnchorHTMLAttributes, ButtonHTMLAttributes, MouseEvent, ReactNode } from 'react'
import { forwardRef } from 'react'
import { Link } from 'react-router-dom'
import type { LinkProps } from 'react-router-dom'
import { cn } from '../lib/cn'
import { FOCUS_RING } from './control-styles'
import { Spinner } from './Spinner'

/*
 * The variant names predate the Untitled UI mapping and are kept, so no call
 * site had to change when the look did. Each one is now exactly one Untitled
 * variant:
 *
 *   primary       Primary
 *   secondary     Secondary gray
 *   ghost         Tertiary gray
 *   link          Link color
 *   link-gray     Link gray
 *   danger        Primary destructive
 *   danger-ghost  Secondary destructive
 */
export type ButtonVariant =
  | 'primary'
  | 'secondary'
  | 'ghost'
  | 'danger'
  | 'danger-ghost'
  | 'link'
  | 'link-gray'

/** 32, 36, 40 and 44 tall. `md` is the header and footer button. */
export type ButtonSize = 'xs' | 'sm' | 'md' | 'lg'

/** An icon button can also be 24, for the copy and trash glyphs inside rows and chips. */
export type IconButtonSize = ButtonSize | '2xs'

/*
 * No `active:translate-y-px` and no opacity on disabled. Untitled has neither,
 * and a disabled button that is only fainter reads as a slow one. Disabled has
 * its own colours in every variant below, and the hover is undone by listing
 * the disabled colour after it, which Tailwind emits later in the sheet.
 */
const BASE =
  'relative inline-flex items-center justify-center whitespace-nowrap rounded-lg font-semibold transition select-none ' +
  'disabled:cursor-not-allowed aria-disabled:cursor-not-allowed ' +
  FOCUS_RING

const VARIANTS: Record<ButtonVariant, string> = {
  /*
   * The fill is spelled `accent`, which is the alias of `primary-600` (and of
   * `primary-700` for the hover). Page contract tests count the resolved
   * `bg-accent` to hold the one-primary rule, so the alias is kept.
   *
   * `text-ink-on` and not `text-white`, still, even though the two now resolve
   * to the same colour. The token says what the label is FOR, which is to read
   * against the accent whatever the accent becomes, and a literal white would
   * have to be found again the day it does not.
   */
  primary:
    'border border-accent bg-accent text-ink-on shadow-xs hover:border-accent-hover hover:bg-accent-hover ' +
    'disabled:border-primary-400 disabled:bg-primary-400 disabled:text-primary-100',
  secondary:
    'border border-line-strong bg-surface text-ink-secondary shadow-xs hover:bg-surface-hover hover:text-ink ' +
    'disabled:border-line disabled:bg-surface disabled:text-ink-faint',
  ghost:
    'text-ink-muted hover:bg-surface-hover hover:text-ink-secondary disabled:bg-transparent disabled:text-ink-faint',
  /**
   * The label is `danger-ink`, the token for ink that has to read on the solid
   * red fill, and the hover is `danger-text`, the family's stronger tone, so
   * the fill moves away from the label rather than toward it.
   */
  danger:
    'border border-danger bg-danger text-danger-ink shadow-xs hover:border-danger-text hover:bg-danger-text ' +
    'disabled:border-danger-soft-border disabled:bg-danger-soft-border disabled:text-white',
  /**
   * Destructive, but not shouting. A solid red block is right for the one
   * button that ends a thing, and wrong for a `Delete` sitting in a row beside
   * `Duplicate`. This is the outline button with the red kept to the edge and
   * the label, which is where the meaning is.
   */
  'danger-ghost':
    'border border-error-300 bg-surface text-danger-text shadow-xs hover:bg-danger-soft hover:text-error-800 ' +
    'disabled:border-danger-soft-border disabled:bg-surface disabled:text-error-300',
  link: 'text-primary-700 hover:text-primary-800 disabled:text-ink-faint',
  'link-gray': 'text-ink-muted hover:text-ink-secondary disabled:text-ink-faint',
}

/**
 * A ghost icon button is quieter than a ghost text button. The close X, the row
 * kebab and the copy glyph sit beside content, and at `ink-muted` a column of
 * them outweighs the text they belong to.
 */
const ICON_GHOST =
  'text-ink-faint hover:bg-surface-hover hover:text-ink-subtle disabled:bg-transparent disabled:text-ink-faint'

const SIZES: Record<ButtonSize, string> = {
  xs: 'h-8 gap-1 px-3 text-base',
  sm: 'h-9 gap-1 px-3.5 text-base',
  md: 'h-10 gap-1.5 px-4 text-base',
  lg: 'h-11 gap-1.5 px-[18px] text-title',
}

/** The link variants have no box at all, only the type size. */
const LINK_SIZES: Record<ButtonSize, string> = {
  xs: 'gap-1 text-base',
  sm: 'gap-1 text-base',
  md: 'gap-1.5 text-base',
  lg: 'gap-1.5 text-title',
}

const ICON_SIZES: Record<IconButtonSize, string> = {
  '2xs': 'h-6 w-6 rounded-md',
  xs: 'h-8 w-8',
  sm: 'h-9 w-9',
  md: 'h-10 w-10',
  lg: 'h-11 w-11',
}

/** The glyph inside a leading or trailing slot. 16 at `xs`, 20 above it. */
const SLOT_GLYPH: Record<IconButtonSize, string> = {
  '2xs': '[&>svg]:h-4 [&>svg]:w-4',
  xs: '[&>svg]:h-4 [&>svg]:w-4',
  sm: '[&>svg]:h-5 [&>svg]:w-5',
  md: '[&>svg]:h-5 [&>svg]:w-5',
  lg: '[&>svg]:h-5 [&>svg]:w-5',
}

/** The glyph that is the whole of an icon button. 16 at the row size, 20 otherwise. */
const ONLY_GLYPH: Record<IconButtonSize, string> = {
  '2xs': '[&>svg]:h-4 [&>svg]:w-4',
  xs: '[&>svg]:h-5 [&>svg]:w-5',
  sm: '[&>svg]:h-5 [&>svg]:w-5',
  md: '[&>svg]:h-5 [&>svg]:w-5',
  lg: '[&>svg]:h-5 [&>svg]:w-5',
}

export interface ButtonBaseProps {
  variant?: ButtonVariant
  size?: ButtonSize
  /**
   * Shows a spinner and ignores clicks. The spinner takes the leading icon's
   * place and the label stays, so the button neither resizes nor goes blank.
   */
  loading?: boolean
  /** Square button holding a single icon. Give it an `aria-label`. */
  iconOnly?: boolean
  leadingIcon?: ReactNode
  trailingIcon?: ReactNode
  fullWidth?: boolean
}

export interface ButtonProps
  extends ButtonBaseProps,
    Omit<ButtonHTMLAttributes<HTMLButtonElement>, 'className'> {
  className?: string
}

interface ClassInput {
  variant?: ButtonVariant
  size?: IconButtonSize
  iconOnly?: boolean
  fullWidth?: boolean
  className?: string
}

function isLink(variant: ButtonVariant): boolean {
  return variant === 'link' || variant === 'link-gray'
}

/** Exported for the contract test, which pins every size and variant to its classes. */
export function buttonClasses({
  variant = 'secondary',
  size = 'md',
  iconOnly = false,
  fullWidth = false,
  className,
}: ClassInput): string {
  // `2xs` exists only for icon buttons, so a text button asked for it gets `xs`.
  const textSize: ButtonSize = size === '2xs' ? 'xs' : size
  return cn(
    BASE,
    iconOnly && variant === 'ghost' ? ICON_GHOST : VARIANTS[variant],
    iconOnly ? ICON_SIZES[size] : isLink(variant) ? LINK_SIZES[textSize] : SIZES[textSize],
    fullWidth && 'w-full',
    className,
  )
}

function Content({
  loading,
  leadingIcon,
  trailingIcon,
  children,
  size = 'md',
  iconOnly,
}: Omit<ButtonBaseProps, 'size'> & { children?: ReactNode; size?: IconButtonSize }) {
  const slot = cn('inline-flex shrink-0', SLOT_GLYPH[size])
  // With a leading icon the spinner simply stands in for it. Without one there
  // is no slot to borrow, so the spinner sits over a hidden label instead and
  // the width still does not move.
  const overlay = loading && (leadingIcon === undefined || leadingIcon === null || iconOnly)
  return (
    <>
      {overlay ? (
        <span className="absolute inset-0 grid place-items-center">
          <Spinner size="sm" label={null} />
        </span>
      ) : null}
      <span
        className={cn(
          'inline-flex items-center gap-[inherit]',
          iconOnly && ONLY_GLYPH[size],
          overlay && 'invisible',
        )}
      >
        {leadingIcon !== undefined && leadingIcon !== null ? (
          <span className={slot}>{loading && !overlay ? <Spinner size="sm" label={null} /> : leadingIcon}</span>
        ) : null}
        {children}
        {trailingIcon !== undefined && trailingIcon !== null ? <span className={slot}>{trailingIcon}</span> : null}
      </span>
    </>
  )
}

/**
 * The standard control. Always a real `<button>` so keyboard and screen readers work.
 *
 * A loading button is `aria-disabled` rather than `disabled`. It keeps its own
 * colours, because a primary that turns pale the moment it is pressed reads as
 * a refusal, and the click is swallowed here instead. Swallowing it with
 * `preventDefault` also stops a submit button from submitting again, including
 * the click a browser synthesises when somebody presses Enter in a field.
 */
export const Button = forwardRef<HTMLButtonElement, ButtonProps>(function Button(
  {
    variant,
    size = 'md',
    loading,
    iconOnly,
    leadingIcon,
    trailingIcon,
    fullWidth,
    className,
    children,
    disabled,
    onClick,
    type = 'button',
    ...rest
  },
  ref,
) {
  const guarded = (event: MouseEvent<HTMLButtonElement>): void => {
    if (loading) {
      event.preventDefault()
      return
    }
    onClick?.(event)
  }
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={guarded}
      className={buttonClasses({ variant, size, iconOnly, fullWidth, className })}
      {...rest}
    >
      <Content
        loading={loading}
        leadingIcon={leadingIcon}
        trailingIcon={trailingIcon}
        size={size}
        iconOnly={iconOnly}
      >
        {children}
      </Content>
    </button>
  )
})

export interface IconButtonProps extends Omit<ButtonProps, 'children' | 'aria-label' | 'iconOnly' | 'size'> {
  /** The accessible name. It is also the tooltip, because the glyph alone never explains itself. */
  label: string
  children: ReactNode
  /** `2xs` is 24, for copy, edit and trash inside rows and chips. */
  size?: IconButtonSize
}

/**
 * A square button whose only content is a glyph. Never unlabelled.
 *
 * Built directly rather than through `Button`, because `Button` only takes the
 * four text sizes and this also takes the 24px row size.
 */
export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(function IconButton(
  {
    label,
    variant = 'ghost',
    size = 'sm',
    children,
    className,
    disabled,
    loading,
    onClick,
    fullWidth,
    leadingIcon: _leadingIcon,
    trailingIcon: _trailingIcon,
    type = 'button',
    ...rest
  },
  ref,
) {
  return (
    <button
      ref={ref}
      type={type}
      disabled={disabled}
      aria-label={label}
      title={label}
      aria-busy={loading || undefined}
      aria-disabled={loading || undefined}
      onClick={(event) => {
        if (loading) {
          event.preventDefault()
          return
        }
        onClick?.(event)
      }}
      className={buttonClasses({ variant, size, iconOnly: true, fullWidth, className })}
      {...rest}
    >
      <Content loading={loading} size={size} iconOnly>
        {children}
      </Content>
    </button>
  )
})

export interface LinkButtonProps extends ButtonBaseProps, Omit<LinkProps, 'className'> {
  className?: string
}

/** A router link that looks like a button. Navigation stays a real link. */
export const LinkButton = forwardRef<HTMLAnchorElement, LinkButtonProps>(function LinkButton(
  { variant, size = 'md', iconOnly, leadingIcon, trailingIcon, fullWidth, className, children, loading: _loading, ...rest },
  ref,
) {
  return (
    <Link ref={ref} className={buttonClasses({ variant, size, iconOnly, fullWidth, className })} {...rest}>
      <Content leadingIcon={leadingIcon} trailingIcon={trailingIcon} size={size} iconOnly={iconOnly}>
        {children}
      </Content>
    </Link>
  )
})

export interface ExternalLinkButtonProps
  extends ButtonBaseProps,
    Omit<AnchorHTMLAttributes<HTMLAnchorElement>, 'className'> {
  className?: string
}

/** For links that leave the console, such as a prospect profile on another site. */
export const ExternalLinkButton = forwardRef<HTMLAnchorElement, ExternalLinkButtonProps>(
  function ExternalLinkButton(
    {
      variant,
      size = 'md',
      iconOnly,
      leadingIcon,
      trailingIcon,
      fullWidth,
      className,
      children,
      loading: _loading,
      ...rest
    },
    ref,
  ) {
    return (
      <a
        ref={ref}
        target="_blank"
        rel="noreferrer noopener"
        className={buttonClasses({ variant, size, iconOnly, fullWidth, className })}
        {...rest}
      >
        <Content leadingIcon={leadingIcon} trailingIcon={trailingIcon} size={size} iconOnly={iconOnly}>
          {children}
        </Content>
      </a>
    )
  },
)

/**
 * A row of buttons with consistent spacing, right aligned by default.
 *
 * This stays a plain row. The joined, one-outline group that switches a view is
 * `SegmentedGroup`, which is a radiogroup and has a different job: a modal
 * footer laid out as a segmented control would read as one choice instead of
 * two actions.
 */
export function ButtonGroup({
  children,
  align = 'end',
  className,
}: {
  children: ReactNode
  align?: 'start' | 'end' | 'between'
  className?: string
}) {
  return (
    <div
      className={cn(
        'flex flex-wrap items-center gap-2',
        align === 'end' && 'justify-end',
        align === 'between' && 'justify-between',
        className,
      )}
    >
      {children}
    </div>
  )
}
